<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { convertAudio } from './utils/mp3ConversionUtils';
import { convertToOpus } from './utils/opusConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'recordError',
  'recordingPaused',
  'recordingResumed',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const hasPreview = ref(false);

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

// Sending finalizes the recording and unmounts this component, which destroys
// wavesurfer and aborts any load still in flight. That rejection is expected.
const loadPreview = audioUrl =>
  wavesurfer.value.load(audioUrl).catch(error => {
    if (error.name !== 'AbortError') throw error;
  });

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#00a884',
    progressColor: '#8696a0',
    cursorWidth: 0,
    height: 32,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    const audioUrl = URL.createObjectURL(blob);
    const isOpus = props.audioRecordFormat === AUDIO_FORMATS.OGG;
    try {
      const audioBlob = isOpus
        ? await convertToOpus(blob)
        : await convertAudio(blob, props.audioRecordFormat);
      const fileName = `${getUuid()}.${isOpus ? 'ogg' : 'mp3'}`;
      const file = new File([audioBlob], fileName, {
        type: props.audioRecordFormat,
      });
      loadPreview(audioUrl);
      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
      hasRecording.value = true;
    } catch (error) {
      // The encoder worker can time out or fail; without this the caller would
      // wait forever for a finishRecord that never arrives.
      emit('recordError', error);
    }
    isRecording.value = false;
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });

  record.value.on('record-pause', async blob => {
    // Load the audio captured so far so the agent can preview while paused
    const audioUrl = URL.createObjectURL(blob);
    await loadPreview(audioUrl);
    hasPreview.value = true;
    emit('recordingPaused');
  });

  record.value.on('record-resume', () => {
    hasPreview.value = false;
    isPlaying.value = false;
    emit('recordingResumed');
  });
};

const stopRecording = () => {
  if (record.value?.isActive()) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const pauseRecording = () => {
  if (record.value?.isRecording()) {
    record.value.pauseRecording();
  }
};

const resumeRecording = () => {
  if (record.value?.isPaused()) {
    if (isPlaying.value) {
      wavesurfer.value.pause();
      isPlaying.value = false;
    }
    hasPreview.value = false;
    record.value.resumeRecording();
  }
};

const startRecording = () => {
  record.value.startRecording();
  isRecording.value = true;
};

const playPause = () => {
  if (hasRecording.value || hasPreview.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({
  playPause,
  stopRecording,
  pauseRecording,
  resumeRecording,
  record,
});
</script>

<template>
  <div ref="waveformContainer" class="w-full h-8" />
</template>
